/**
 * GlobalDocumentNotice — the single, app-wide host for the bottom-right
 * document-generation card. Mounted once in App so a render started
 * anywhere (wizard, Logic Engine test run, an automation trigger with no
 * button pressed at all) always shows progress, on whatever screen the
 * user happens to be on.
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import DocumentGenerationNotice from "@/components/wizard/DocumentGenerationNotice";
import { subscribeDocuments, trackDocuments, dismissDocuments } from "@/lib/docNoticeBus";

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 4000;

export default function GlobalDocumentNotice() {
  const [docs, setDocs] = useState([]);

  useEffect(() => subscribeDocuments(setDocs), []);

  // Pick up renders this user has in flight that nothing on screen queued.
  useEffect(() => {
    let alive = true;
    const tick = async () => {
      const token = localStorage.getItem("compass_token");
      if (!token) return;
      try {
        const r = await axios.get(`${API}/api/logic/documents/in-flight`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (alive) trackDocuments(r.data?.documents || []);
      } catch {
        // Signed out / offline — the next tick tries again.
      }
    };
    tick();
    const id = setInterval(tick, POLL_MS);
    return () => { alive = false; clearInterval(id); };
  }, []);

  const headers = useMemo(
    () => ({ Authorization: `Bearer ${localStorage.getItem("compass_token") || ""}` }),
    [docs.length]  // eslint-disable-line react-hooks/exhaustive-deps
  );

  if (!docs.length) return null;

  return (
    <DocumentGenerationNotice
      pendingDocs={docs}
      headers={headers}
      onDismiss={dismissDocuments}
    />
  );
}
